import { initFeatureFlags } from "../featureFlagsService";
import { getApiBearerToken } from "./tokenService";
import { getUserData, getUserDataApp } from "./authBootstrapService";

// Bootstrap intentionally runs in two phases so we can separate identity setup
// from heavier app-level network calls and keep login/callback paths deterministic.
export const AUTH_BOOTSTRAP_PHASES = {
  IDENTITY: "identity",
  APP_DATA: "app-data",
};

// Tracks completed/in-flight phases to prevent duplicate startup work when
// callback, hydration, and App effects fire close together.
const completedPhases = new Set();
const inFlightPhases = new Map();
// Phases that failed are recorded so repeated event-driven callers (tab focus,
// storage events) do not hammer the backend with the same doomed request.
// Only an explicit reset (logout/re-login) clears this set.
const failedPhases = new Set();

const executeTasks = async (tasks = []) => {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return;
  }

  const taskPromises = tasks
    .filter((task) => typeof task === "function")
    .map((task) => {
      return Promise.resolve().then(() => task());
    });

  const results = await Promise.allSettled(taskPromises);
  results.forEach((result) => {
    if (result.status === "rejected") {
      // Non-critical app bootstrap tasks should not block auth completion.
      console.error("Auth bootstrap task failed.", result.reason);
    }
  });
};

// Guarantees each phase runs at most once per app boot while also allowing
// concurrent callers to await the same in-flight Promise.
const runPhaseOnce = async (phase, handler) => {
  if (completedPhases.has(phase) || failedPhases.has(phase)) {
    return;
  }

  if (inFlightPhases.has(phase)) {
    await inFlightPhases.get(phase);
    return;
  }

  const phasePromise = (async () => {
    await handler();
    completedPhases.add(phase);
  })();

  inFlightPhases.set(phase, phasePromise);

  try {
    await phasePromise;
  } catch (error) {
    // Record the failure so event-driven callers stop retrying until the
    // user explicitly re-authenticates (which calls reset).
    failedPhases.add(phase);
    throw error;
  } finally {
    inFlightPhases.delete(phase);
  }
};

const hasValidUserAndToken = async (userManager) => {
  if (!userManager) {
    return false;
  }

  const user = await userManager.getUser();
  const accessToken = getApiBearerToken();
  return user?.expired === false && !!accessToken;
};

export const bootstrapAuthenticatedSession = async ({
  phase,
  userManager,
  forgeRockUserData,
  queryClient,
  tasks,
}) => {
  if (!phase) {
    return;
  }

  if (phase === AUTH_BOOTSTRAP_PHASES.IDENTITY) {
    // Identity phase writes Engage user/session data to localStorage.
    // It can be driven from either OIDC callback (userManager) or storage
    // hydration (already-resolved ForgeRock profile).
    await runPhaseOnce(AUTH_BOOTSTRAP_PHASES.IDENTITY, async () => {
      if (forgeRockUserData) {
        await getUserDataApp(forgeRockUserData);
        return;
      }

      if (userManager) {
        await getUserData(userManager);
      }
    });

    return;
  }

  // Resolves true once app data is ready (run now or earlier) and false when
  // skipped for lack of a valid session, so callers don't start post-sign-in
  // work (Nitro) on a boot that is about to redirect to sign-in.
  if (phase === AUTH_BOOTSTRAP_PHASES.APP_DATA) {
    // App-data phase should only run once the auth session is fully valid.
    const canBootstrap = await hasValidUserAndToken(userManager);
    if (!canBootstrap) {
      return false;
    }

    // Wait for identity phase to complete before running app-data tasks.
    // This ensures the user record exists in the DB (created by handleUserData)
    // before endpoints like getAnnouncements try to look up the user.
    if (inFlightPhases.has(AUTH_BOOTSTRAP_PHASES.IDENTITY)) {
      await inFlightPhases.get(AUTH_BOOTSTRAP_PHASES.IDENTITY);
    }

    await runPhaseOnce(AUTH_BOOTSTRAP_PHASES.APP_DATA, async () => {
      // These two run concurrently. They used to be sequential, which meant an
      // avatar and a notification badge gated the feature flags that decide what
      // UI renders at all — neither task is an input to the flags, and the flag
      // fetch does not need anything they produce.
      //
      // Caller provides optional startup tasks so App decides what to preload.
      const startupTasks = executeTasks(tasks);

      const appConfiguration =
        queryClient && userManager
          ? initFeatureFlags(queryClient, userManager)
          : Promise.resolve();

      // executeTasks resolves even when its tasks fail (it logs and swallows),
      // so this rejects only for a flag failure.
      await Promise.all([startupTasks, appConfiguration]);
    });
    return true;
  }
};

// Called on logout so a subsequent login in the same SPA session can re-run
// both phases from a clean slate.
export const resetAuthenticatedSessionBootstrap = () => {
  completedPhases.clear();
  inFlightPhases.clear();
  failedPhases.clear();
};

// Clears a single phase from the failed set so it can be retried. Used by
// callback completion to retry a transient identity bootstrap failure without
// resetting all phases.
export const clearFailedBootstrapPhase = (phase) => {
  failedPhases.delete(phase);
};
